/**
 * Restoration mirror for an explicit Pickup choice (mirrors `checkout-address-session`).
 *
 * Runtime source of truth is Redux `checkoutUx.fulfilment`; this only lets an explicit Pickup
 * choice survive a new document in the same tab session (reload, same-tab redirects).
 * Delivery is the default, so it is never stored: choosing Delivery removes the entry.
 *
 * Key is restaurant-scoped (`rx-checkout-fulfilment-v1:{restaurantId}`); the value carries the
 * owning customer id so a restore requires both scopes to match. No cart, address or customer
 * data. Every failure is silent: a corrupted entry can only mean "nothing to restore".
 */

const CHECKOUT_FULFILMENT_SESSION_PREFIX = "rx-checkout-fulfilment-v1:"

export type CheckoutFulfilmentSessionEntry = {
  userId: string
  fulfilment: "PICKUP"
}

function canUseSessionStorage(): boolean {
  try {
    return typeof sessionStorage !== "undefined"
  } catch {
    return false
  }
}

function normalizeId(value: unknown): string | null {
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  return trimmed ? trimmed : null
}

export function checkoutFulfilmentSessionKey(restaurantId: string): string | null {
  const rid = normalizeId(restaurantId)
  return rid ? `${CHECKOUT_FULFILMENT_SESSION_PREFIX}${rid}` : null
}

/** Stored Pickup choice for `restaurantId`, or null when absent, malformed, or storage is unavailable. */
export function readCheckoutFulfilmentSession(restaurantId: string): CheckoutFulfilmentSessionEntry | null {
  if (!canUseSessionStorage()) return null
  const key = checkoutFulfilmentSessionKey(restaurantId)
  if (!key) return null
  try {
    const raw = sessionStorage.getItem(key)
    if (typeof raw !== "string" || !raw) return null
    const parsed = JSON.parse(raw) as unknown
    const userId = normalizeId((parsed as { userId?: unknown } | null)?.userId)
    const fulfilment = (parsed as { fulfilment?: unknown } | null)?.fulfilment
    if (!userId || fulfilment !== "PICKUP") {
      sessionStorage.removeItem(key)
      return null
    }
    return { userId, fulfilment: "PICKUP" }
  } catch {
    return null
  }
}

export function writeCheckoutFulfilmentSession(restaurantId: string, userId: string): void {
  if (!canUseSessionStorage()) return
  const key = checkoutFulfilmentSessionKey(restaurantId)
  const uid = normalizeId(userId)
  if (!key || !uid) return
  try {
    sessionStorage.setItem(key, JSON.stringify({ userId: uid, fulfilment: "PICKUP" }))
  } catch {
    /* private mode / quota — restoration is best-effort */
  }
}

export function clearCheckoutFulfilmentSession(restaurantId: string): void {
  if (!canUseSessionStorage()) return
  const key = checkoutFulfilmentSessionKey(restaurantId)
  if (!key) return
  try {
    sessionStorage.removeItem(key)
  } catch {
    /* ignore */
  }
}

/** Restaurant change, successful order, logout: drop every restaurant's entry. */
export function clearAllCheckoutFulfilmentSessions(): void {
  if (!canUseSessionStorage()) return
  try {
    const keys: string[] = []
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i)
      if (key && key.startsWith(CHECKOUT_FULFILMENT_SESSION_PREFIX)) keys.push(key)
    }
    for (const key of keys) sessionStorage.removeItem(key)
  } catch {
    /* ignore */
  }
}
